import type { Pathname } from '../../navigation/routes.gen';

// Extracts parameter names (like 'userId') from a route path string (like '/users/$userId')
/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
type ExtractParams<T extends string> = T extends `${infer _Start}$${infer Param}/${infer Rest}`
    ? Param | ExtractParams<`/${Rest}`>
    : /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
      T extends `${infer _Start}$${infer Param}`
      ? Param
      : never;

type hasToString = {
    toString: () => string | hasToString;
};

type Params<P extends Pathname> = Record<ExtractParams<P>, string | hasToString>;

export function generateHref<P extends Pathname>({
    to,
    params,
    searchParams,
    baseUrl: baseUrlParam,
}: {
    baseUrl?: string;
    to: P;
    searchParams?: Record<string, string | hasToString>;
} & (ExtractParams<P> extends never ? { params?: never } : { params: Params<P> })) {
    const baseUrl = baseUrlParam ?? process.env.APP_BASE_URL;

    let result: string = to;

    // --- Parameter Processing ---
    // Find all placeholders like $paramName in the route path.
    const placeholders = result.match(/\$(\w+)/g) || [];

    if (placeholders.length) {
        // If there are placeholders, TypeScript's types *require* `params` to be defined
        // and non-null if RequiresParams<T> was true.
        // We add runtime checks for robustness against type bypasses or unexpected JS behavior.
        if (!params || typeof params !== 'object') {
            // This state should be theoretically unreachable with correct TS usage
            // for routes that require params.
            throw new Error(
                `generateHref: Route path "${to}" requires parameters, but 'params' object was missing or invalid.`,
            );
        }

        // Cast params for easier access (TS already ensures it's the correct type)
        const paramObj = params as Params<P>; // Type assertion safe due to GenerateLinkOptions

        result = result.replace(/\$(\w+)/g, (match, paramName) => {
            const key = paramName as keyof Params<P>;

            // Runtime check: Ensure the specific key exists in the provided params object.
            // Even if TS required 'params', it doesn't prevent calling with `{ ... } as any`
            if (Object.hasOwn(paramObj, key)) {
                const value = paramObj[key];
                // Ensure value is a string. Handle potential null/undefined if types were somehow bypassed.
                if (value !== null && value !== undefined) {
                    return String(value);
                } else {
                    throw new Error(
                        `generateHref: Missing required parameter value for "${paramName}" in route path "${to}". Provided params: ${JSON.stringify(
                            params,
                        )}`,
                    );
                }
            } else {
                // **CRITICAL FAILURE**: A placeholder exists in the path,
                // but the required key was missing from the 'params' object.
                // This signifies a mismatch that should have ideally been caught by TypeScript.
                throw new Error(
                    `generateHref: Missing required parameter value for "${paramName}" in route path "${to}". Provided params: ${JSON.stringify(
                        params,
                    )}`,
                );
            }
        });
    }
    // No need for a post-replacement check for remaining '$' if we throw errors above,
    // as any missing parameter would have already caused the function to fail.

    if (searchParams && Object.keys(searchParams).length > 0) {
        const searchParamsString = new URLSearchParams(
            Object.entries(searchParams).map(([key, value]) => {
                const stringValue = typeof value === 'string' ? value : value.toString();
                return [key, stringValue];
            }) as string[][],
        ).toString();

        // Avoid double '?' if the path somehow ended with one (unlikely)
        result = result.replace(/\?$/, '');
        result = `${result}?${searchParamsString}`;
    }

    return `${baseUrl}${result}`;
}
